import { useNearbyWorkers } from "@crm/api";
import { MapPin, Navigation, Loader2, AlertCircle, User } from "lucide-react";
import { cn } from "@crm/utils";

interface NearbyWorkersProps {
  incidentId: string;
  onAssign?: (userId: string) => void;
  isAssigning?: boolean;
}

export function NearbyWorkers({
  incidentId,
  onAssign,
  isAssigning,
}: NearbyWorkersProps) {
  const { data, isLoading, error } = useNearbyWorkers(incidentId, {
    limit: 10,
  });

  if (isLoading) {
    return (
      <div className="rounded-lg border border-border bg-card p-6">
        <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-foreground">
          <Navigation className="h-4 w-4" />
          Nearby Workers
        </h3>
        <div className="flex h-32 items-center justify-center">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-border bg-card p-6">
        <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-foreground">
          <Navigation className="h-4 w-4" />
          Nearby Workers
        </h3>
        <div className="flex flex-col items-center justify-center py-6 text-center">
          <AlertCircle className="h-8 w-8 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            {error instanceof Error && error.message.includes("No location")
              ? "No location set for this incident"
              : "Failed to load nearby workers"}
          </p>
        </div>
      </div>
    );
  }

  const { incident_location, workers } = data || { workers: [] };

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <h3 className="mb-4 flex items-center gap-2 text-sm font-semibold text-foreground">
        <Navigation className="h-4 w-4" />
        Nearby Workers
      </h3>

      {/* Location info */}
      {incident_location && (
        <div className="mb-4 flex items-start gap-2 rounded-md bg-muted/50 p-3 text-xs">
          <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-foreground">
              {incident_location.address || "No address"}
            </p>
            <p className="text-muted-foreground">
              {incident_location.latitude.toFixed(4)},{" "}
              {incident_location.longitude.toFixed(4)}
              <span className="ml-2 text-muted-foreground/70">
                (from{" "}
                {incident_location.source === "incident"
                  ? "incident"
                  : "client"}
                )
              </span>
            </p>
          </div>
        </div>
      )}

      {/* Workers list */}
      {workers.length === 0 ? (
        <div className="py-6 text-center">
          <User className="mx-auto h-8 w-8 text-muted-foreground mb-2" />
          <p className="text-sm text-muted-foreground">
            No workers currently sharing location
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {workers.map((worker, index) => (
            <div
              key={worker.user_id}
              className={cn(
                "flex items-center justify-between rounded-md border p-3 transition-colors",
                index === 0
                  ? "border-green-200 bg-green-50"
                  : "border-border hover:bg-muted/50"
              )}
            >
              <div className="flex items-center gap-3">
                {/* Rank badge */}
                <div
                  className={cn(
                    "flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold",
                    index === 0
                      ? "bg-green-600 text-white"
                      : index === 1
                      ? "bg-gray-400 text-white"
                      : index === 2
                      ? "bg-amber-600 text-white"
                      : "bg-gray-200 text-gray-600"
                  )}
                >
                  {index + 1}
                </div>

                {/* Worker info */}
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {worker.first_name} {worker.last_name}
                  </p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {worker.role}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-3">
                {/* Distance */}
                <div className="text-right">
                  <p className="text-sm font-semibold text-foreground">
                    {worker.distance_km < 1
                      ? `${Math.round(worker.distance_km * 1000)} m`
                      : `${worker.distance_km.toFixed(1)} km`}
                  </p>
                  <p className="text-xs text-muted-foreground">away</p>
                </div>

                {/* Assign button */}
                {onAssign && (
                  <button
                    onClick={() => onAssign(worker.user_id)}
                    disabled={isAssigning}
                    className="rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
                  >
                    Assign
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
